// One drag-and-drop context for the whole workspace, so a task dragged from any list or board can
// land on a sidebar area, space, project, or Inbox, and sidebar rows can be reordered. Lists,
// boards, and the sidebar describe their draggables and drop targets with `data`; this provider
// routes each drop by those payloads and draws the lifted overlay.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
  type Modifier,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { STATUS_LABEL, type Destination, type TaskStatus, type TaskWithFlags } from "@desk/contracts";
import { describeError } from "@/data/api";
import { useApi } from "@/data/context";
import { writeContainers, type ContainerKind } from "@/data/containers";
import { useHierarchy, type Hierarchy } from "@/data/hooks";
import { useTaskActions } from "@/data/actions";
import { taskRegistry } from "@/features/list/registry";
import { ui } from "@/features/ui-state";
import { applyBulk } from "@/features/selection/BulkBar";
import { captureSelection } from "@/features/selection/selection";
import { positionAt } from "@/lib/ordering";
import { cn } from "@/lib/utils";

// ───────────────────────── payloads ─────────────────────────

/** Something being dragged. */
export type DragItem =
  | { type: "task"; listId: string; task: TaskWithFlags }
  | {
      type: "container";
      kind: ContainerKind;
      id: string;
      /** Sibling group: "areas", "top" (top-level spaces), "area:<id>", or "space:<id>". */
      parent: string;
      name: string;
    };

/** Something that can be dropped on. Draggable rows are drop targets too. */
export type DropData = DragItem | { type: "dest"; id: "inbox"; label: string } | { type: "toplevel" } | { type: "column"; listId: string; status: TaskStatus };

/** How a drop target should look right now. */
export type DropState = "into" | "before" | "after" | null;

export function parentOf(kind: ContainerKind, areaId?: string | null, spaceId?: string | null): string {
  if (kind === "area") return "areas";
  if (kind === "project") return `space:${spaceId}`;
  return areaId ? `area:${areaId}` : "top";
}

/** Can a dragged container land on this target? */
export function canDropContainer(item: Extract<DragItem, { type: "container" }>, target: DropData | undefined): boolean {
  if (!target) return false;
  if (target.type === "toplevel") return item.kind === "space";
  if (target.type !== "container" || target.id === item.id) return false;
  if (item.kind === "area") return target.kind === "area";
  // A space reorders among spaces anywhere (moving areas when the parent differs) or drops into an area.
  if (item.kind === "space") return target.kind === "space" || target.kind === "area";
  // Projects can't change space through an update; they reorder within their space.
  return target.kind === "project" && target.parent === item.parent;
}

/** Whether dropping `item` on `target` means "move into it" rather than "reorder beside it". */
export function isInto(item: DragItem, target: DropData): boolean {
  if (target.type === "dest" || target.type === "toplevel" || target.type === "column") return true;
  if (item.type === "task") return target.type === "container";
  return item.kind === "space" && target.type === "container" && target.kind === "area";
}

/** The destination a task gets when dropped on a sidebar row or Inbox. */
export function destinationOf(target: DropData, h: Hierarchy): { dest: Destination; label: string } | null {
  if (target.type === "dest") return { dest: { areaId: null, spaceId: null, projectId: null }, label: target.label };
  if (target.type !== "container") return null;
  if (target.kind === "area") return { dest: { areaId: target.id, spaceId: null, projectId: null }, label: target.name };
  if (target.kind === "space") return { dest: { areaId: h.space(target.id)?.areaId ?? null, spaceId: target.id, projectId: null }, label: target.name };
  const p = h.project(target.id);
  const space = h.space(p?.spaceId);
  return { dest: { areaId: space ? space.areaId : p?.areaId ?? null, spaceId: p?.spaceId ?? null, projectId: target.id }, label: target.name };
}

// ───────────────────────── context ─────────────────────────

interface DragView {
  active: DragItem | null;
  overId: string | null;
  over: DropData | null;
  /** For reorders: the drop lands after the target (pointer below its middle). */
  after: boolean;
}

type ListHandler = (activeId: string, overId: string) => void;

interface Registry {
  lists: Map<string, ListHandler>;
}

const ViewContext = createContext<DragView>({ active: null, overId: null, over: null, after: false });
const RegistryContext = createContext<Registry>({ lists: new Map() });

export const useDragView = () => useContext(ViewContext);

/** Lists register how to apply a reorder inside them. */
export function useListReorder(listId: string, handler: ListHandler | undefined) {
  const reg = useContext(RegistryContext);
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!handler) return;
    reg.lists.set(listId, (a, o) => ref.current?.(a, o));
    return () => void reg.lists.delete(listId);
  }, [reg, listId, !!handler]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Drop state for a target id: "into" for moves, a before/after line for reorders. */
export function useDropState(id: string): DropState {
  const v = useDragView();
  if (!v.active || v.overId !== id || !v.over) return null;
  if (v.active.type === "container" && `${v.active.kind}:${v.active.id}` === id) return null;
  if (isInto(v.active, v.over)) return "into";
  // Task reorders inside a list show the sortable gap instead of a line.
  if (v.active.type === "task") return null;
  return v.after ? "after" : "before";
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

// ───────────────────────── collisions and keyboard ─────────────────────────

const dataOf = (c: { data: { current?: unknown } }) => c.data.current as DropData | undefined;

export const deskCollision: CollisionDetection = (args) => {
  const item = args.active.data.current as DragItem | undefined;
  const only = (pred: (d: DropData | undefined) => boolean) => ({ ...args, droppableContainers: args.droppableContainers.filter((c) => pred(dataOf(c))) });
  if (item?.type === "task") {
    // A sidebar target under the pointer wins: that's a move.
    const sidebar = pointerWithin(only((d) => d?.type === "dest" || d?.type === "container"));
    if (sidebar.length) return sidebar;
    const columns = only((d) => d?.type === "column" && d.listId === item.listId);
    if (columns.droppableContainers.length) {
      const hit = pointerWithin(columns);
      return hit.length ? hit : rectIntersection(columns);
    }
    return closestCenter(only((d) => d?.type === "task" && d.listId === item.listId));
  }
  if (item?.type === "container") {
    // The source row is a valid collision but a no-op drop. Excluding it makes even a pickup
    // without movement target the nearest sibling and unexpectedly reorder the sidebar.
    const ok = only((d) => (d?.type === "container" && d.kind === item.kind && d.id === item.id) || canDropContainer(item, d));
    const hit = pointerWithin(ok);
    return hit.length ? hit : closestCenter(ok);
  }
  return closestCenter(args);
};

/** Sortable lists step between items; sidebar rows step one row at a time. */
const keyboardCoordinates: KeyboardCoordinateGetter = (event, args) => {
  const item = args.context.active?.data.current as DragItem | undefined;
  if (item?.type === "task") {
    const { context, currentCoordinates } = args;
    // Board cards are draggable, not sortable: they have no active droppable entry for the
    // sortable getter. Step through this board's columns, including columns with no cards.
    if (!context.droppableContainers.get(args.active)) {
      if (event.code !== "ArrowLeft" && event.code !== "ArrowRight") return;
      const collision = context.collisionRect;
      if (!collision) return;
      const columns = context.droppableContainers.getEnabled()
        .filter((c) => { const d = dataOf(c); return d?.type === "column" && d.listId === item.listId; })
        .map((c) => ({ data: dataOf(c) as Extract<DropData, { type: "column" }>, rect: context.droppableRects.get(c.id) }))
        .filter((c) => !!c.rect)
        .sort((a, b) => a.rect!.left - b.rect!.left);
      const over = context.over?.data.current as DropData | undefined;
      const status = over?.type === "column" && over.listId === item.listId ? over.status : item.task.status;
      const index = columns.findIndex((c) => c.data.status === status);
      const next = index < 0 ? undefined : columns[index + (event.code === "ArrowRight" ? 1 : -1)]?.rect;
      event.preventDefault();
      if (!next) return;
      return {
        x: currentCoordinates.x + next.left + (next.width - collision.width) / 2 - collision.left,
        y: currentCoordinates.y + next.top + (next.height - collision.height) / 2 - collision.top,
      };
    }
    return sortableKeyboardCoordinates(event, args);
  }
  const step = 31;
  const { x, y } = args.currentCoordinates;
  if (event.code === "ArrowDown") return { x, y: y + step };
  if (event.code === "ArrowUp") return { x, y: y - step };
  return undefined;
};

const centerY = (r: { top: number; height: number } | null | undefined) => (r ? r.top + r.height / 2 : 0);

/** Keep the lifted copy just below and right of the pointer, so it never covers the row being targeted. */
const followPointer: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !activatorEvent || !("clientX" in activatorEvent)) return transform;
  const e = activatorEvent as PointerEvent;
  return { ...transform, x: transform.x + e.clientX - draggingNodeRect.left + 12, y: transform.y + e.clientY - draggingNodeRect.top + 14 };
};

// ───────────────────────── provider ─────────────────────────

export function DeskDndProvider({ children }: { children: ReactNode }) {
  const registry = useMemo<Registry>(() => ({ lists: new Map() }), []);
  const [view, setView] = useState<DragView>({ active: null, overId: null, over: null, after: false });
  const h = useHierarchy();
  const a = useTaskActions();
  const api = useApi();
  const qc = useQueryClient();
  const reduced = usePrefersReducedMotion();
  const suppressClickUntil = useRef(0);
  const announcements = useAnnouncements();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: keyboardCoordinates,
      // Space picks up and drops; Enter also drops. Enter on a focused sidebar link still navigates.
      keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space", "Enter"] },
    }),
  );

  // A pointer drag ends with a click on whatever is under the pointer; don't let it navigate or select.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (Date.now() < suppressClickUntil.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("click", onClick, true);
    return () => window.removeEventListener("click", onClick, true);
  }, []);

  const end = () => {
    suppressClickUntil.current = Date.now() + 120;
    document.body.classList.remove("desk-dragging");
    ui.set({ dragging: false });
    setView({ active: null, overId: null, over: null, after: false });
  };

  const onDragStart = (e: DragStartEvent) => {
    document.body.classList.add("desk-dragging");
    ui.set({ dragging: true });
    setView({ active: (e.active.data.current as DragItem) ?? null, overId: null, over: null, after: false });
  };

  const onDragOver = (e: DragOverEvent) => {
    const after = centerY(e.active.rect.current.translated) > centerY(e.over?.rect);
    setView((v) => ({ ...v, overId: e.over ? String(e.over.id) : null, over: (e.over?.data.current as DropData) ?? null, after }));
  };
  const onDragMove = (e: DragMoveEvent) => {
    if (!e.over) return;
    const after = centerY(e.active.rect.current.translated) > centerY(e.over.rect);
    setView((v) => (v.after === after ? v : { ...v, after }));
  };

  const draggedTasks = (task: TaskWithFlags) => {
    const selected = ui.get().selectedIds;
    const ids = selected.includes(task.id) && selected.length > 1 ? selected : [task.id];
    return ids.map((id) => taskRegistry.get(id) ?? (id === task.id ? task : undefined)).filter((t): t is TaskWithFlags => !!t);
  };

  /** Move the dragged task (or the whole multi-selection containing it) into a container. */
  const moveTasks = async (task: TaskWithFlags, target: DropData) => {
    const to = destinationOf(target, h);
    if (!to) return;
    const tasks = draggedTasks(task);
    const same = (t: TaskWithFlags) =>
      (t.projectId ?? null) === (to.dest.projectId ?? null) && (t.spaceId ?? null) === (to.dest.spaceId ?? null) && (t.areaId ?? null) === (to.dest.areaId ?? null);
    const moving = tasks.filter((t) => !same(t));
    if (!moving.length) return;
    const ownsSelection = captureSelection();
    const results = await Promise.all(moving.map((t) => a.move(t, to.dest)));
    const failed = moving.filter((_, i) => results[i]!.status === "failed");
    const moved = moving.filter((_, i) => results[i]!.status !== "failed");
    if (failed.length && ownsSelection()) ui.set({ selectedIds: failed.map((t) => t.id), selectedTaskId: failed[0]!.id, selectionAnchor: failed[0]!.id });
    else if (!failed.length && ownsSelection()) ui.set({ selectedIds: [], selectedTaskId: null, selectionAnchor: null });
    if (!moved.length) return;
    const what = moved.length === 1 ? (moved[0]!.readableId === "DSK-0" ? "task" : moved[0]!.readableId) : `${moved.length} tasks`;
    toast(`Moved ${what} to ${to.label}`, {
      action: {
        label: "Undo",
        onClick: () => void Promise.all(moved.map((t) => a.move(t, { areaId: t.areaId, spaceId: t.spaceId, projectId: t.projectId }))),
      },
    });
  };

  /** Reorder or move a sidebar container. One update of the moved item, applied optimistically. */
  const dropContainer = async (item: Extract<DragItem, { type: "container" }>, target: DropData, after: boolean) => {
    let parent = item.parent;
    let index: number;
    let siblings: { id: string; position: number }[];
    const listOf = (p: string) =>
      p === "areas" ? h.areas : p === "top" ? h.topSpaces : p.startsWith("area:") ? h.spacesOf(p.slice(5)) : h.projectsOf(p.slice(6));
    if (target.type === "toplevel") {
      parent = "top";
      siblings = listOf(parent);
      index = siblings.length;
    } else if (target.type === "container" && item.kind === "space" && target.kind === "area") {
      parent = `area:${target.id}`;
      siblings = listOf(parent);
      index = siblings.filter((s) => s.id !== item.id).length;
    } else if (target.type === "container") {
      parent = target.parent;
      siblings = listOf(parent);
      const rest = siblings.filter((s) => s.id !== item.id);
      index = rest.findIndex((s) => s.id === target.id) + (after ? 1 : 0);
    } else return;
    const before = siblings.findIndex((s) => s.id === item.id);
    if (parent === item.parent && before === index) return;
    const position = positionAt(siblings, item.id, index);
    const areaId = parent === "top" ? null : parent.startsWith("area:") ? parent.slice(5) : undefined;
    const moved = item.kind === "space" && parent !== item.parent;
    const patch: Record<string, unknown> = { position, ...(moved ? { areaId } : {}) };
    try {
      await writeContainers<unknown>(
        qc,
        () =>
          item.kind === "area"
            ? api.updateArea(item.id, { position })
            : item.kind === "space"
              ? api.updateSpace(item.id, moved ? { position, areaId } : { position })
              : api.updateProject(item.id, { position }),
        [{ kind: item.kind, id: item.id, patch }],
      );
      if (moved) toast(`Moved ${item.name} ${areaId ? `to ${h.area(areaId)?.name ?? "the area"}` : "to the top level"}`);
    } catch (err) {
      // The overlay patch is gone, so the row is already back where it was.
      toast.error(moved ? `Couldn't move ${item.name}` : "Couldn't save the order", { description: describeError(err) });
    }
  };

  const onDragEnd = (e: DragEndEvent) => {
    const item = e.active.data.current as DragItem | undefined;
    const target = e.over?.data.current as DropData | undefined;
    const after = centerY(e.active.rect.current.translated) > centerY(e.over?.rect);
    end();
    if (!item || !target || !e.over) return;
    if (item.type === "task") {
      if (target.type === "dest" || target.type === "container") return void moveTasks(item.task, target);
      if (target.type === "column") {
        const tasks = draggedTasks(item.task).filter((t) => t.status !== target.status);
        void applyBulk(tasks, (t) => a.setStatus(t, target.status), {
          done: (n) => `Set ${n} to ${STATUS_LABEL[target.status].toLowerCase()}`,
          keep: target.status !== "done" && target.status !== "cancelled",
        });
        return;
      }
      if (target.type === "task" && target.listId === item.listId && e.active.id !== e.over.id) {
        registry.lists.get(item.listId)?.(String(e.active.id), String(e.over.id));
      }
      return;
    }
    if (canDropContainer(item, target)) void dropContainer(item, target, after);
  };

  return (
    <RegistryContext.Provider value={registry}>
      <ViewContext.Provider value={view}>
        <DndContext
          sensors={sensors}
          collisionDetection={deskCollision}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
          onDragCancel={end}
          accessibility={{ announcements }}
        >
          {children}
          {createPortal(
            <DragOverlay
              modifiers={[followPointer]}
              dropAnimation={reduced ? null : { duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" }}
              zIndex={60}
              className="!h-auto !w-auto"
            >
              {view.active ? <DragPreview item={view.active} view={view} h={h} /> : null}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </ViewContext.Provider>
    </RegistryContext.Provider>
  );
}

function useAnnouncements() {
  const name = useCallback((d: unknown) => {
    const x = d as DropData | undefined;
    if (!x) return "";
    if (x.type === "task") return x.task.title;
    if (x.type === "column") return STATUS_LABEL[x.status];
    if (x.type === "toplevel") return "Top level";
    if (x.type === "container" || x.type === "dest") return x.type === "dest" ? x.label : x.name;
    return "";
  }, []);
  return useMemo(
    () => ({
      onDragStart: ({ active }: { active: { data: { current?: unknown } } }) => `Picked up ${name(active.data.current)}.`,
      onDragOver: ({ active, over }: { active: { data: { current?: unknown } }; over: { data: { current?: unknown } } | null }) =>
        over ? `${name(active.data.current)} is over ${name(over.data.current)}.` : `${name(active.data.current)} is not over a drop target.`,
      onDragEnd: ({ active, over }: { active: { data: { current?: unknown } }; over: { data: { current?: unknown } } | null }) =>
        over ? `Dropped ${name(active.data.current)} on ${name(over.data.current)}.` : `Dropped ${name(active.data.current)}.`,
      onDragCancel: ({ active }: { active: { data: { current?: unknown } } }) => `Cancelled. ${name(active.data.current)} was not moved.`,
    }),
    [name],
  );
}

/** The lifted copy under the pointer. A pending move says where it goes. */
function DragPreview({ item, view, h }: { item: DragItem; view: DragView; h: Hierarchy }) {
  const into = view.over && isInto(item, view.over) ? view.over : null;
  const dest = into && item.type === "task" ? destinationOf(into, h) : null;
  const selected = ui.get().selectedIds;
  const count = item.type === "task" && selected.includes(item.task.id) ? selected.length : 1;
  return (
    <div
      className={cn(
        "desk-drag-preview pointer-events-none flex max-w-[360px] items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-[14px] text-foreground shadow-pop",
        item.type === "container" && "max-w-[260px] py-1.5 text-[13.5px]",
      )}
    >
      <span className="min-w-0 truncate">{item.type === "task" ? item.task.title : item.name}</span>
      {count > 1 && <span className="shrink-0 rounded-full bg-foreground px-1.5 text-[11.5px] font-medium leading-[18px] text-background tabular">{count}</span>}
      {dest && <span className="shrink-0 whitespace-nowrap text-[12px] text-muted-foreground">→ {dest.label}</span>}
    </div>
  );
}
